import {
    FUND_AUTHORIZATION_REQUIRED_CODE,
    FUND_AUTHORIZATION_REQUIRED_STATUS,
} from './constants';
import { fundAuthorizationRequiredResponseSchema } from './schemas';
import type {
    BridgeIntentDescriptor,
    FundAuthorizationDescriptor,
    TransferFees,
} from './types';

/**
 * The fund-authorization handshake's phase-1 answer, surfaced as a throwable
 * so `runWithFundAuthorization` can catch it around the start call. The
 * backend delivers it as a NORMAL HTTP 200 body (`status:
 * 'fundAuthorizationRequired'`, `code: 'FUND_AUTHORIZATION_REQUIRED'`,
 * `required: FundAuthorizationDescriptor[]`) instead of a job response — see
 * `fundAuthorizationRequiredResponse` in
 * `MoonHub.API/managers/SpeedTradingJobManager.ts` — so the start services
 * translate that body into this error before response-schema parsing.
 *
 * A withdrawal challenge also carries `bridgeIntent`: the bridge hop the user's
 * own wallet has to register on chain before the proof is minted. It is
 * undefined for deposits and agent setup, and for a backend that predates the
 * registry.
 *
 * It may also carry `fees`: what the backend just quoted for that hop. The
 * challenge is raised AFTER the route is quoted and BEFORE any prompt opens, so
 * this is the one moment the real cost of a withdrawal exists while the user
 * can still walk away from it. Optional in the same way and for the same
 * reason as `bridgeIntent`, and never load-bearing: it is a display number.
 */
export class FundAuthorizationRequiredChallenge extends Error {
    readonly code = FUND_AUTHORIZATION_REQUIRED_CODE;

    constructor(
        readonly required: FundAuthorizationDescriptor[],
        readonly bridgeIntent?: BridgeIntentDescriptor,
        readonly fees?: TransferFees,
    ) {
        super(
            'The server requires fresh user fund authorizations for this ' +
                'operation.',
        );
        this.name = 'FundAuthorizationRequiredChallenge';
    }
}

function isChallengeBody(data: unknown): boolean {
    if (typeof data !== 'object' || data === null) {
        return false;
    }
    const body = data as { code?: unknown; status?: unknown };
    return (
        body.code === FUND_AUTHORIZATION_REQUIRED_CODE ||
        body.status === FUND_AUTHORIZATION_REQUIRED_STATUS
    );
}

/**
 * Throw {@link FundAuthorizationRequiredChallenge} when a start-response body
 * is the handshake's challenge. A body that CLAIMS to be a challenge but whose
 * descriptor list does not validate throws a plain error instead — never sign
 * what cannot be validated.
 */
export function throwIfFundAuthorizationChallenge(data: unknown): void {
    if (!isChallengeBody(data)) {
        return;
    }
    const parsed = fundAuthorizationRequiredResponseSchema.safeParse(data);
    if (!parsed.success) {
        throw new Error(
            'The server required a fund authorization but described the ' +
                'operations in an unexpected shape; refusing to sign.',
        );
    }
    throw new FundAuthorizationRequiredChallenge(
        parsed.data.required,
        parsed.data.bridgeIntent,
        parsed.data.fees,
    );
}
